import { 
  DirectionsBus, 
  Build, 
  Storefront,
  SvgIconComponent,
  Flight,
  RestaurantMenu,
  School,
  EventNote,
  LocalParking
} from '@mui/icons-material';

// Service configuration type
export interface ServiceConfig {
  id: string;
  name: string;
  icon: SvgIconComponent;
  route: string;
  description: string;
  requiresPremium?: boolean;
  enabled: boolean;
  category?: 'transport' | 'equipment' | 'marketplace' | 'booking' | 'education' | 'food';
}

// Centralized service configuration
export const SERVICES: Record<string, ServiceConfig> = {
  shuttle: {
    id: 'shuttle',
    name: 'serviceAuth.shuttleBookingService',
    icon: DirectionsBus,
    route: '/app/shuttle-booking',
    description: 'services.shuttle.description',
    requiresPremium: false,
    enabled: true,
    category: 'transport'
  },
  equipment: {
    id: 'equipment',
    name: 'serviceAuth.equipmentReminderService',
    icon: Build,
    route: '/app/reminders/dashboard',
    description: 'services.equipment.description',
    requiresPremium: false,
    enabled: true,
    category: 'equipment'
  },
  marketplace: {
    id: 'marketplace',
    name: 'serviceAuth.marketplace',
    icon: Storefront,
    route: '/app/marketplace',
    description: 'Buy and sell flight equipment',
    requiresPremium: false,
    enabled: true,
    category: 'marketplace'
  },
  // Future services can be easily added here
  flight_booking: {
    id: 'flight_booking',
    name: 'serviceAuth.flightBookingService',
    icon: Flight,
    route: '/flight-booking',
    description: 'Book tandem flights and courses',
    requiresPremium: true,
    enabled: false, // Set to true when ready
    category: 'booking'
  },
  restaurant: {
    id: 'restaurant',
    name: 'serviceAuth.restaurantService',
    icon: RestaurantMenu,
    route: '/restaurant',
    description: 'Reserve tables at launch site restaurants',
    requiresPremium: false,
    enabled: false,
    category: 'food'
  },
  training: {
    id: 'training',
    name: 'serviceAuth.trainingService',
    icon: School,
    route: '/training',
    description: 'Book training sessions and courses',
    requiresPremium: true,
    enabled: false,
    category: 'education'
  },
  events: {
    id: 'events',
    name: 'serviceAuth.eventsService',
    icon: EventNote,
    route: '/events',
    description: 'Register for competitions and events',
    requiresPremium: false,
    enabled: false,
    category: 'booking'
  },
  parking: {
    id: 'parking',
    name: 'serviceAuth.parkingService',
    icon: LocalParking,
    route: '/parking',
    description: 'Reserve parking spots at launch sites',
    requiresPremium: false,
    enabled: false,
    category: 'transport'
  }
};

// Get only enabled services
export const getEnabledServices = (): ServiceConfig[] => {
  return Object.values(SERVICES).filter(service => service.enabled);
};

// Get service by ID
export const getServiceById = (id: string): ServiceConfig | undefined => {
  return SERVICES[id];
};

// Get services by category
export const getServicesByCategory = (category: string): ServiceConfig[] => {
  return Object.values(SERVICES).filter(
    service => service.enabled && service.category === category
  );
};

// Type for service intents
export type ServiceIntent = keyof typeof SERVICES;

// Helper to check if a string is a valid service intent
export const isValidServiceIntent = (intent: string): intent is ServiceIntent => {
  return intent in SERVICES && SERVICES[intent as ServiceIntent].enabled;
};

// Get route for a service
export const getServiceRoute = (intent: ServiceIntent): string => {
  return SERVICES[intent]?.route || '/services';
};

// Get icon component for a service
export const getServiceIcon = (intent: ServiceIntent): SvgIconComponent | null => {
  return SERVICES[intent]?.icon || null;
};